import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Smartphone, Images, ArrowRight } from "lucide-react";
import { BACKEND, api } from "@/lib/api";

export const DEMO_SLUG = "jan-in-mateja-demo";
const HERO = "https://images.unsplash.com/photo-1606490194859-07c18c9f0968?w=1600&q=85&fm=jpg";

const rise = (d = 0) => ({ initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.8, delay: d } });

export function Hero() {
  return (
    <section className="pt-28 sm:pt-32 pb-20 lg:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center" data-testid="hero-section">
      <motion.div {...rise()} className="lg:col-span-6 space-y-8">
        <p className="eyebrow">Poročna galerija s QR kodo</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.02]" data-testid="hero-headline">Vsi vajini poročni spomini. <em className="text-[#B39264] not-italic font-normal">Na enem mestu.</em></h1>
        <p className="text-base sm:text-lg text-[#6C6560] max-w-xl leading-relaxed" data-testid="hero-subtitle">En QR, nešteto spominov. Naj vajini gostje delijo fotografije in videe brez aplikacije in registracije.</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/ustvari" className="btn-dark px-7 py-4 text-sm sm:text-base flex items-center gap-2" data-testid="hero-cta-create-wedding">Ustvarita svojo poroko <ArrowRight size={18} /></Link>
          <a href="#kako-deluje" className="btn-line px-7 py-4 text-sm sm:text-base" data-testid="hero-cta-how-it-works">Kako deluje?</a>
          <Link to={`/poroka/${DEMO_SLUG}`} className="px-5 py-4 text-sm sm:text-base underline underline-offset-4 decoration-[#C5A880] hover:text-[#B39264]" data-testid="hero-cta-try-demo">Preizkusi demo</Link>
        </div>
      </motion.div>
      <motion.div {...rise(0.15)} className="lg:col-span-6 relative">
        <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] sm:aspect-[5/5] lg:aspect-[4/5] grain shadow-[0_30px_80px_rgba(35,31,32,0.15)]">
          <img src={HERO} alt="Mladoporočenca ob sončnem zahodu" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-8 -left-4 sm:-left-10 card-lux p-4 flex items-center gap-4 bg-[#FFFDF9]/95 backdrop-blur" data-testid="hero-qr-card">
          <img src={`${BACKEND}/api/public/w/${DEMO_SLUG}/qr.png`} alt="Demo QR koda" className="w-20 h-20 sm:w-24 sm:h-24" />
          <div className="pr-2">
            <p className="font-display text-xl">Jan & Mateja</p>
            <p className="text-xs text-[#6C6560] max-w-[10rem]">Skeniraj s telefonom in preizkusi demo galerijo.</p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

const STEPS = [
  [QrCode, "Ustvarita poroko", "V nekaj minutah uredita svojo galerijo in prejmeta unikatno QR kodo za tisk ali na naših namiznih karticah in tabli dobrodošlice."],
  [Smartphone, "Gostje skenirajo", "Kamera telefona odpre vajino galerijo. Brez aplikacije, brez gesla — vpišejo le ime in priimek."],
  [Images, "Vsi spomini skupaj", "Fotografije in videi v originalni kakovosti se pojavijo v skupni galeriji. Prenesita vse kot ZIP."],
];

export function HowItWorks() {
  return (
    <section id="kako-deluje" className="py-20 sm:py-28 bg-[#F3ECE1]/50 scroll-mt-16" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="eyebrow">Kako deluje</p>
        <h2 className="font-display text-4xl sm:text-5xl font-light mt-3 max-w-2xl">Trije preprosti koraki do vseh poročnih spominov</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {STEPS.map(([Icon, t, d], i) => (
            <motion.div key={t} {...rise(i * 0.1)} className="card-lux p-8 space-y-5" data-testid={`how-step-${i + 1}`}>
              <div className="flex items-center justify-between">
                <span className="w-12 h-12 rounded-full bg-[#F3ECE1] grid place-items-center text-[#B39264]"><Icon size={22} /></span>
                <span className="font-display text-5xl text-[#EADBCE]">0{i + 1}</span>
              </div>
              <h3 className="font-display text-2xl">{t}</h3>
              <p className="text-[#6C6560] leading-relaxed text-sm sm:text-base">{d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function QrDemo() {
  return (
    <section className="py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center" data-testid="qr-demo-section">
      <div className="space-y-6 order-2 lg:order-1">
        <p className="eyebrow">Preizkusite v živo</p>
        <h2 className="font-display text-4xl sm:text-5xl font-light">Skenirajte kodo — tako jo vidijo vaši gostje</h2>
        <p className="text-[#6C6560] leading-relaxed max-w-lg">To je prava QR koda demo poroke Jana in Mateje. Usmerite kamero telefona vanjo ali pa uporabite telefon na desni. Lahko dodate fotografijo, pustite srček ali zapišete sporočilo v knjigo gostov.</p>
        <div className="flex items-center gap-6">
          <img src={`${BACKEND}/api/public/w/${DEMO_SLUG}/qr.png`} alt="QR koda demo poroke" className="w-40 h-40 rounded-2xl bg-white p-2 border border-[#E8DFC5]" data-testid="qr-demo-code" />
          <div className="space-y-3">
            <Link to={`/poroka/${DEMO_SLUG}`} className="btn-dark px-6 py-3 text-sm inline-block" data-testid="qr-demo-scan-button">Odpri demo galerijo</Link>
            <button onClick={() => api.get("/public/demo-wall").then((r) => window.open(`/zaslon/${r.data.token}`, "_blank"))} className="block text-sm underline underline-offset-4 decoration-[#C5A880]" data-testid="qr-demo-slideshow-link">Poglej Live Wall v živo</button>
          </div>
        </div>
      </div>
      <div className="order-1 lg:order-2 flex justify-center">
        <div className="w-[300px] h-[610px] rounded-[3rem] bg-[#231F20] p-3 shadow-[0_40px_90px_rgba(35,31,32,0.25)]" data-testid="qr-demo-phone">
          <iframe title="Demo poroka" src={`/poroka/${DEMO_SLUG}`} className="w-full h-full rounded-[2.4rem] bg-[#FAF7F2]" loading="lazy" />
        </div>
      </div>
    </section>
  );
}
